// FILE: src/pages/provider-management/components/AvailabilityCalendar.jsx
import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import dayjs from 'dayjs';
import jalaliday from 'jalaliday';
import api from 'utils/api';
import { useProviderStore } from 'Store/ProviderStore';
import Icon from '../../../components/AppIcon';
import SendPaymentModal from '../../../components/modals/SendPaymentModal'; // Import Modal

dayjs.extend(jalaliday);

const AvailabilityCalendar = () => {
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [bookings, setBookings] = useState([]);
  const [employees, setEmployees] = useState([]);

  // State for Booking Detail Modal
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // State for Payment Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const provider = useProviderStore((state) => state.provider);

  const persianDays = [
    'شنبه',
    'یکشنبه',
    'دوشنبه',
    'سه‌شنبه',
    'چهارشنبه',
    'پنج‌شنبه',
    'جمعه',
  ];

  // ... (توابع تاریخ و هفته مثل قبل باقی می‌مانند)
  const getCurrentWeekDates = () => {
    const today = new Date();
    const currentDay = today.getDay();
    const daysToSubtract = (currentDay + 1) % 7;
    const saturday = new Date(today);
    saturday.setDate(today.getDate() - daysToSubtract);
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(saturday);
      date.setDate(saturday.getDate() + i + selectedWeek * 7);
      weekDates.push(date);
    }
    return weekDates;
  };
  const weekDates = getCurrentWeekDates();

  // Fetch Data logic (همانند قبل)
  useEffect(() => {
    if (!provider?._id) return;
    const fetchEmployees = async () => {
      try {
        const res = await api.get(`/providers/${provider._id}/employees`);
        setEmployees([
          { label: 'همه', value: 'all' },
          ...res.data.map((emp) => ({ label: emp.name, value: emp._id })),
        ]);
      } catch (err) {
        console.error(err);
      }
    };
    fetchEmployees();
  }, [provider?._id]);

  const fetchBookings = async () => {
    if (!provider?._id) return;
    try {
      const url =
        selectedEmployee === 'all'
          ? `/availabilities/${provider._id}`
          : `/providers/${provider._id}/employees/${selectedEmployee}/availability`;
      const res = await api.get(url);
      setBookings(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [selectedEmployee, provider?._id, selectedWeek]);

  const getSlotColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'pending_payment':
        return 'bg-blue-100 text-blue-700 border-blue-200'; // وضعیت جدید
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'در انتظار بررسی';
      case 'pending_payment':
        return 'منتظر پرداخت';
      case 'confirmed':
        return 'نهایی شده';
      case 'cancelled':
        return 'لغو شده';
      default:
        return status;
    }
  };

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
    setShowDetailModal(true);
  };

  // هندلر باز کردن مودال پرداخت
  const handleOpenPayment = () => {
    setShowDetailModal(false); // بستن جزئیات
    setShowPaymentModal(true); // باز کردن پرداخت
  };

  const handlePaymentSuccess = () => {
    fetchBookings(); // رفرش دیتا
  };

  // ... (توابع رندر جدول همانند قبل)
  const getBookingsForDate = (date, empId) => {
    const targetDate = dayjs(date);
    return bookings.filter((b) => {
      const bookingEmpId = b.employee?._id || b.employee;
      if (selectedEmployee !== 'all' && String(bookingEmpId) !== String(empId))
        return false;
      // اگر همه انتخاب شده بود، باید چک کنیم که این رزرو مال این کارمند سطر جاری است یا نه
      if (selectedEmployee === 'all' && String(bookingEmpId) !== String(empId))
        return false;

      return dayjs(b.start).isSame(targetDate, 'day');
    });
  };

  const shownEmployees =
    selectedEmployee === 'all'
      ? employees.filter((e) => e.value !== 'all')
      : employees.filter((e) => e.value === selectedEmployee);

  return (
    <div className="space-y-6">
      {/* Header & Controls (Same as before) */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            تقویم رزروها
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSelectedWeek(selectedWeek - 1)}
            iconName="ChevronRight"
          />
          <span className="text-sm font-mono dir-ltr">
            {dayjs(weekDates[0]).format('YYYY/MM/DD')}
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSelectedWeek(selectedWeek + 1)}
            iconName="ChevronLeft"
          />
        </div>
      </div>

      <div className="w-full max-w-xs">
        <Select
          options={employees}
          value={selectedEmployee}
          onChange={setSelectedEmployee}
        />
      </div>

      {/* Calendar Grid */}
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <div className="min-w-[1000px]">
          <div className="grid grid-cols-8 border-b border-border bg-muted/30">
            <div className="p-4 font-medium text-sm">کارمند</div>
            {persianDays.map((day, i) => (
              <div
                key={day}
                className="p-4 text-center border-r border-border/50 text-sm">
                <div className="font-bold">{day}</div>
                <div className="text-xs text-muted-foreground">
                  {dayjs(weekDates[i]).calendar('jalali').format('D MMM')}
                </div>
              </div>
            ))}
          </div>

          {shownEmployees.map((emp) => (
            <div
              key={emp.value}
              className="grid grid-cols-8 border-b border-border hover:bg-muted/5">
              <div className="p-4 flex items-center border-l border-border/50 text-sm font-medium">
                {emp.label}
              </div>
              {weekDates.map((date, i) => {
                const slots = getBookingsForDate(date, emp.value);
                return (
                  <div
                    key={i}
                    className="p-2 border-r border-border/50 min-h-[100px] flex flex-col gap-2">
                    {slots.map((slot) => (
                      <button
                        key={slot._id}
                        onClick={() => handleSlotClick(slot)}
                        className={`w-full text-right p-2 rounded border text-xs shadow-sm hover:shadow-md transition-all ${getSlotColor(
                          slot.status
                        )}`}>
                        <div className="font-bold mb-1">
                          {dayjs(slot.start).format('HH:mm')}
                        </div>
                        <div className="truncate">{slot.clientName}</div>
                        <div className="text-[10px] opacity-80 mt-1">
                          {getStatusLabel(slot.status)}
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* --- Booking Detail Modal --- */}
      {showDetailModal && selectedSlot && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6 shadow-xl animate-in fade-in zoom-in-95">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-lg font-bold text-foreground">
                  جزئیات رزرو
                </h3>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${getSlotColor(
                    selectedSlot.status
                  )}`}>
                  {getStatusLabel(selectedSlot.status)}
                </span>
              </div>
              <Button
                size="icon"
                variant="ghost"
                iconName="X"
                onClick={() => setShowDetailModal(false)}
              />
            </div>

            <div className="space-y-4 text-sm">
              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">مشتری:</span>
                  <span className="font-medium">{selectedSlot.clientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">تماس:</span>
                  <a
                    href={`tel:${selectedSlot.clientPhone}`}
                    className="font-medium text-primary hover:underline dir-ltr">
                    {selectedSlot.clientPhone}
                  </a>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">سرویس:</span>
                  <span className="font-medium">
                    {selectedSlot.service?.name}
                  </span>
                </div>
              </div>

              <div className="flex justify-between items-center py-2 border-t border-b border-border">
                <span className="text-muted-foreground">زمان:</span>
                <span className="font-medium dir-ltr">
                  {dayjs(selectedSlot.start).format('HH:mm')} -{' '}
                  {dayjs(selectedSlot.start)
                    .calendar('jalali')
                    .format('YYYY/MM/DD')}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="grid gap-3 pt-2">
                {selectedSlot.status === 'pending' && (
                  <Button
                    variant="default"
                    className="w-full bg-primary"
                    iconName="CreditCard"
                    onClick={handleOpenPayment}>
                    ارسال لینک پرداخت (SMS)
                  </Button>
                )}

                {selectedSlot.status === 'pending_payment' && (
                  <div className="text-center text-xs text-warning bg-warning/10 p-2 rounded">
                    لینک پرداخت ارسال شده است. منتظر پرداخت مشتری...
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => {
                      if (window.confirm('آیا مطمئن هستید؟')) {
                        // call cancel api
                        // refresh
                      }
                    }}>
                    لغو نوبت
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      window.location.href = `tel:${selectedSlot.clientPhone}`;
                    }}>
                    تماس با مشتری
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Payment Modal Integration --- */}
      <SendPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        booking={selectedSlot}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default AvailabilityCalendar;
